<template>
  <stepped-modal
    :close-disabled="savingKeywords"
    :steps="steps"
    :current-step="currentStep"
    @closed="closeChosenKeywordsModal"
  >
    <template #header>
      <div
        v-if="currentStep.name !== 'review'"
        class="chosen_keywords"
      >
        <span>
          {{ totalKeywords }}/{{ keywordLimit }} Chosen Keywords
        </span>
        <span v-if="canPurchaseKeywords">
          (<a
            class="order_keywords"
            href="/keywords/order"
            target="_blank"
          >+Upgrade</a>)
        </span>
      </div>
    </template>

    <template #footer>
      <button
        v-if="canEditKeywords && currentStep.name === 'overview'"
        class="bulk_add"
        @click="openBulkAddModal"
      >
        + Bulk add keywords
      </button>
    </template>
  </stepped-modal>
</template>

<script>
import Swal from 'sweetalert2';
import {mapActions, mapGetters, mapMutations} from 'vuex';
import ConfirmStep from './steps/ConfirmStep.vue';
import OverviewStep from './steps/OverviewStep.vue';
import ReviewChoiceStep from "./steps/ReviewChoiceStep.vue";
import {SET_REVIEW_ENABLED} from "../../../../../../js/modules/keywords/keywordIdeas";
import SteppedModal from "../../../../../../js/components/global/modals/SteppedModal.vue";
import useKit from '../../../../../../js/composables/useKit';

export default {
  components: {
    SteppedModal,
  },
  emits: ['updatedKeywords'],
  setup() {
    const { isSubscribedToKit } = useKit();
    return { isSubscribedToKit };
  },
  data() {
    return {
      currentStepIndex: 0,
      bulkKeywords: '',
      error: null,
    }
  },
  computed: {
    ...mapGetters('keywordIdeas', [
      'keywordLimit',
      'chosenKeywords',
      'newChosenKeywords',
      'removeChosenKeywords',
      'reviewEnabled',
      'pendingKeywordReviews',
      'onboarding',
      'keywordIdsForReview',
      'savingKeywords',
      'loadingExistingReviews',
      'existingReviews',
    ]),
    ...mapGetters('user', [
      'canEditKeywords',
      'user',
    ]),
    canPurchaseKeywords() {
      return window.merchantOfferings.isEnabled('KEYWORDS');
    },
    totalKeywords() {
      return this.chosenKeywords.length + this.newChosenKeywords.length;
    },
    isProcessing() {
      return this.loadingExistingReviews
        || this.savingKeywords
        || (this.reviewEnabled && this.pendingKeywordReviews.length > 0);
    },
    saveButtonText() {
      if (this.savingKeywords) {
        return 'Saving';
      }

      if (this.onboarding) {
        return 'Save And Go To Next Step';
      }

      if (this.newChosenKeywords.length > 0 || this.removeChosenKeywords.length > 0) {
        return 'Save And Go To Sitemap';
      }

      return 'Go To Sitemap';
    },
    currentStep() {
      return this.steps[this.currentStepIndex];
    },
    reviewStepActive() {
      return !this.isSubscribedToKit && (this.onboarding || this.keywordIdsForReview.length > 0);
    },
    steps() {
      return [
        {
          name: 'overview',
          label: 'Keywords',
          title: 'Check your chosen keywords',
          component: OverviewStep,
          buttons: [
            {
              label: 'Close',
              class: 'secondary_btn',
              action: () => this.closeChosenKeywordsModal(),
            },
            {
              label: 'Next',
              class: 'primary_btn',
              disabled: () => !this.onboarding && this.isProcessing,
              action: () => this.setStep(this.reviewStepActive ? 'review' : 'confirm'),
              visible: () => this.canEditKeywords,
            }
          ]
        },
        {
          name: 'review',
          label: 'Review',
          title: null,
          component: ReviewChoiceStep,
          hidden: () => !this.reviewStepActive,
          buttons: [
            {
              label: 'No',
              class: 'secondary_btn',
              disabled: () => this.reviewEnabled,
              action: () => this.skipAutomatedReview(),
            },
            {
              label: 'Yes',
              class: 'primary_btn',
              disabled: () => this.reviewEnabled || !this.user.active_website.industry,
              loading: () => this.reviewEnabled,
              action: () => this.startAutomatedReview(),
            }
          ]
        },
        {
          name: 'confirm',
          label: 'Confirm',
          title: 'Confirm changes to chosen keywords',
          component: ConfirmStep,
          buttons: [
            {
              label: 'Back',
              class: 'secondary_btn',
              disabled: () => this.savingKeywords,
              action: () => this.goToOverview(),
            },
            {
              label: this.saveButtonText,
              class: 'primary_btn',
              disabled: () => {
                return this.onboarding
                  && this.newChosenKeywords.length === 0;
              },
              loading: () => this.savingKeywords,
              action: () => this.save(),
            }
          ]
        },
      ];
    }
  },
  async mounted() {
    if (!this.existingReviews) {
      await this.fetchExistingReviews();
    }
  },
  methods: {
    ...mapActions('keywordIdeas', [
      'updateKeywords',
      'openBulkAddModal',
      'closeChosenKeywordsModal',
      'startKeywordReview',
      'resetKeywordReview',
      'fetchExistingReviews',
    ]),
    ...mapMutations('keywordIdeas', {
      setReviewEnabled: SET_REVIEW_ENABLED,
    }),
    async goToOverview() {
      if (this.reviewEnabled) {
        await this.resetKeywordReview();
      }
      this.setStep('overview');
    },
    async startAutomatedReview() {
      await this.startKeywordReview(this.keywordIdsForReview)

      this.setStep('confirm');
    },
    setStep(name) {
      if (name === 'review' && this.isSubscribedToKit) {
        name = 'confirm';
      }
      this.currentStepIndex = this.steps.findIndex(step => step.name === name);
    },
    skipAutomatedReview() {
      this.setReviewEnabled(false);
      this.setStep('confirm');
    },
    async save() {
      // Confirm going to sitemap if there are pending reviews.
      if (this.reviewEnabled && this.pendingKeywordReviews.length > 0) {
        const {isDenied, isDismissed } = await Swal.fire({
          icon: 'warning',
          title: 'Are you sure?',
          html: 'Your keyword reviews haven\'t completed yet, but they will keep processing so you can revisit this page later.',
          showCancelButton: true,
          confirmButtonText: 'Continue',
          confirmButtonColor: 'var(--primary-color)',
          showLoaderOnConfirm: false,
          allowOutsideClick: false,
          allowEnterKey: false,
          reverseButtons: true,
        });

        if (isDenied || isDismissed) {
          return;
        }
      }

      // We can exit early here and just redirect if there are no changes and not onboarding.
      if (!this.onboarding && this.newChosenKeywords.length === 0 && this.removeChosenKeywords.length === 0) {
        await this.closeChosenKeywordsModal();
        window.location.href = '/sitemap';
        return;
      }

      const data = {
        new_keywords: this.newChosenKeywords,
        keywords_to_remove: this.removeChosenKeywords,
      };

      this.error = null;
      try {
        await this.updateKeywords(data);
        this.$emit('updatedKeywords');
        await this.closeChosenKeywordsModal();

        if (!this.onboarding) {
          window.location.href = '/sitemap';
        }
      } catch(e) {
        this.error = e.data.message;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.chosen_keywords,
.order_keywords {
  letter-spacing: 0.00834em;
  font-weight: 400;
  color: #37B2A8;
  line-height: 1;
}

.order_keywords {
  text-decoration: underline;
}

.order_keywords[target="_blank"] {
  padding: 0;
}

.order_keywords[target="_blank"]:after {
  background-image: none;
  margin: 0;
  top: 0;
}

.bulk_add {
  padding-top: 0;
  padding-bottom: 0;
  font-size: 0.75rem;
  line-height: 1.334;
  letter-spacing: 0.00834em;
  font-weight: 400;
  color: var(--primary-color);
  cursor: pointer;
}
</style>
